import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import moment from 'moment'

import api from '../../services/api'
import useModal from '../../hooks/useModal'
import * as S from '../modals/Modal.styles'

import { ReactComponent as CloseIcon } from '../../assets/images/close_delete_icon.svg'

const ROLE = {
  ASSISTANT: 'ASSISTANT',
  USER: 'USER',
}

const CONTEXT = {
  MAIN: 'MAIN',
}

function pad(value) {
  return String(value).padStart(2, '0')
}

function formatAbsoluteTime(dateString) {
  if (!dateString) return ''

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) return ''

  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())

  return `${year}.${month}.${day} ${hours}:${minutes}`
}

function formatRelativeTime(dateString) {
  if (!dateString) return ''

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) {
    return dateString
  }

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  if (diffMs < 0) return '방금 전'

  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return '방금 전'
  if (diffMin < 60) return `${diffMin}분 전`
  if (diffHour < 24) return `${diffHour}시간 전`
  if (diffDay <= 30) return `${diffDay}일 전`

  return formatAbsoluteTime(dateString)
}

function formatDisplayTime(dateString) {
  return {
    relative: formatRelativeTime(dateString),
    absolute: formatAbsoluteTime(dateString),
  }
}

function formatMessageTime(dateString) {
  if (!dateString) return ''

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) {
    return dateString
  }

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${hours}:${minutes}`
}

function sortChatRoomsByLatest(chatRooms) {
  return [...chatRooms].sort((a, b) => {
    const aTime = new Date(
      a.lastMessageCreatedAt || a.updatedAt || 0
    ).getTime()
    const bTime = new Date(
      b.lastMessageCreatedAt || b.updatedAt || 0
    ).getTime()

    return bTime - aTime
  })
}

function moveChatRoomToTop(chatRooms, targetChatId, updater) {
  const targetChat = chatRooms.find(
    chat => chat.mentoringConversationId === targetChatId
  )

  if (!targetChat) return chatRooms

  const updatedChat = updater ? updater(targetChat) : targetChat
  const rest = chatRooms.filter(
    chat => chat.mentoringConversationId !== targetChatId
  )

  return [updatedChat, ...rest]
}

export default function AiMentoringModal() {
  const { openModal, closeModal } = useModal()

  const [chatRooms, setChatRooms] = useState([])
  const [selectedChatId, setSelectedChatId] = useState(null)
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState([])
  const [typingChatId, setTypingChatId] = useState(null)
  const [, setNowTick] = useState(0)

  const chatBodyRef = useRef(null)
  const selectedChatIdRef = useRef(null)
  const messageRequestSeqRef = useRef(0)
  const touchedChatIdsRef = useRef(new Set())

  useEffect(() => {
    selectedChatIdRef.current = selectedChatId
  }, [selectedChatId])

  useEffect(() => {
    if (!chatBodyRef.current) return

    chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
  }, [messages, typingChatId])

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(prev => prev + 1)
    }, 60 * 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    async function fetchData() {
      try {
        const result = await api.get('/mentoring/conversations', {
          params: { context: CONTEXT.MAIN },
        })

        const list = result.data.mentoringConversationList
        const sortedList = sortChatRoomsByLatest(list)

        setChatRooms(sortedList)

        if (sortedList.length > 0) {
          const firstChatId = sortedList[0].mentoringConversationId
          touchedChatIdsRef.current.add(firstChatId)
          setSelectedChatId(firstChatId)

          const messageResult = await api.get(
            `/mentoring/conversations/${firstChatId}/messages`
          )
          setMessages(messageResult.data.mentoringConversationMessageList)
        }
      } catch (e) {
        openModal({
          type: 'alert',
          props: {
            isError: true,
            contents: e.response?.data?.message || e.message,
          },
        })
      }
    }

    fetchData()
  }, [])

  const selectedChatRoom = useMemo(() => {
    return chatRooms.find(chat => chat.mentoringConversationId === selectedChatId)
  }, [chatRooms, selectedChatId])

  const isStreamingCurrentChat = selectedChatId !== null && typingChatId === selectedChatId

  const handleSelectChat = async chatId => {
    touchedChatIdsRef.current.add(chatId)
    const requestSeq = ++messageRequestSeqRef.current

    try {
      setSelectedChatId(chatId)

      const result = await api.get(`/mentoring/conversations/${chatId}/messages`)

      if (requestSeq !== messageRequestSeqRef.current) return
      if (selectedChatIdRef.current !== chatId) return

      setMessages(result.data.mentoringConversationMessageList)
    } catch (e) {
      openModal({
        type: 'alert',
        props: {
          isError: true,
          contents: e.response?.data?.message || e.message,
        },
      })
    }
  }

  const handleCreateChat = async () => {
    const { mentoringConversationId } = await api
      .post('/mentoring/conversations', { context: CONTEXT.MAIN })
      .then(result => result.data)

    const nowIso = new Date().toISOString()

    const newChat = {
      mentoringConversationId,
      title: '새 대화',
      preview: '새로운 멘토링 대화를 시작해 보세요',
      updatedAt: nowIso,
      lastMessageCreatedAt: nowIso,
    }

    touchedChatIdsRef.current.add(newChat.mentoringConversationId)
    setChatRooms(prev => [newChat, ...prev])
    setMessages([])
    setTypingChatId(null)
    setSelectedChatId(newChat.mentoringConversationId)
  }

  async function handleCloseModal() {
    const touchedChatIdList = Array.from(touchedChatIdsRef.current)

    try {
      await Promise.allSettled(
        touchedChatIdList.map(chatId =>
          api.post(`/mentoring/conversations/${chatId}/closures`)
        )
      )
    } catch (e) {
      openModal({
        type: 'alert',
        props: {
          isError: true,
          contents: e.response?.data?.message || e.message,
        },
      })
    } finally {
      closeModal()
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const trimmedMessage = message.trim()
    if (!trimmedMessage || !selectedChatId || isStreamingCurrentChat) return

    const currentSelectedChatId = selectedChatId

    const chatRoom = chatRooms.find(
      chat => chat.mentoringConversationId === currentSelectedChatId
    )

    const tempUserMessageId = `user-${Date.now()}`
    const tempAssistantMessageId = `assistant-${Date.now()}`
    const nowIso = new Date().toISOString()

    setMessages(prev => [
      ...prev,
      {
        mentoringMessageId: tempUserMessageId,
        role: ROLE.USER,
        content: trimmedMessage,
        createdAt: nowIso,
      },
    ])

    setChatRooms(prev =>
      moveChatRoomToTop(prev, currentSelectedChatId, chat => ({
        ...chat,
        preview: trimmedMessage,
        updatedAt: nowIso,
        lastMessageCreatedAt: nowIso,
      }))
    )

    setMessage('')
    setTypingChatId(currentSelectedChatId)

    try {
      let processedLength = 0
      let buffer = ''
      let assistantContent = ''
      let nextOpenaiResponseId = chatRoom?.openaiResponseId
      let hasInsertedAssistantMessage = false

      const applyAssistantContent = content => {
        if (selectedChatIdRef.current !== currentSelectedChatId) return

        setMessages(prev => {
          const exists = prev.some(
            msg => msg.mentoringMessageId === tempAssistantMessageId
          )

          if (!exists) {
            return [
              ...prev,
              {
                mentoringMessageId: tempAssistantMessageId,
                role: ROLE.ASSISTANT,
                content,
                createdAt: nowIso,
              },
            ]
          }

          return prev.map(msg =>
            msg.mentoringMessageId === tempAssistantMessageId
              ? { ...msg, content }
              : msg
          )
        })

        requestAnimationFrame(() => {
          if (!chatBodyRef.current) return
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
        })
      }

      const stopTypingIfCurrentChat = () => {
        setTypingChatId(prev =>
          prev === currentSelectedChatId ? null : prev
        )
      }

      const handleSseText = text => {
        buffer += text

        const chunks = buffer.split('\n\n')
        buffer = chunks.pop() || ''

        for (const chunk of chunks) {
          const lines = chunk.split('\n')

          let eventType = 'message'
          const dataLines = []

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim()
              continue
            }

            if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).replace(/^\s/, ''))
            }
          }

          if (!dataLines.length) continue

          const rawData = dataLines.join('\n')

          if (eventType === 'delta') {
            if (!hasInsertedAssistantMessage) {
              hasInsertedAssistantMessage = true
              stopTypingIfCurrentChat()
            }

            assistantContent += rawData
            applyAssistantContent(assistantContent)
            continue
          }

          if (eventType === 'meta') {
            const parsed = JSON.parse(rawData)

            if (parsed.responseId) {
              nextOpenaiResponseId = parsed.responseId
            }

            if (parsed.openaiResponseId) {
              nextOpenaiResponseId = parsed.openaiResponseId
            }

            continue
          }

          if (eventType === 'title') {
            const parsed = JSON.parse(rawData)
            const title = parsed.title?.trim()

            if (!title) continue

            setChatRooms(prev =>
              moveChatRoomToTop(prev, currentSelectedChatId, chat => ({
                ...chat,
                title,
              }))
            )
          }

          if (eventType === 'completed') {
            const parsed = JSON.parse(rawData)

            if (!hasInsertedAssistantMessage) {
              hasInsertedAssistantMessage = true
              stopTypingIfCurrentChat()
            }

            if (parsed.fullText) {
              assistantContent = parsed.fullText
              applyAssistantContent(assistantContent)
            }

            if (parsed.responseId) {
              nextOpenaiResponseId = parsed.responseId
            }

            if (parsed.openaiResponseId) {
              nextOpenaiResponseId = parsed.openaiResponseId
            }
          }
        }
      }

      await api.request({
        method: 'post',
        url: `/mentoring/study/conversations/${currentSelectedChatId}/messages`,
        data: {
          content: trimmedMessage,
          previousResponseId: chatRoom?.openaiResponseId,
        },
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        responseType: 'text',
        onDownloadProgress: progressEvent => {
          const xhr = progressEvent?.event?.target
          const responseText = xhr?.responseText

          if (!responseText) return

          const newText = responseText.slice(processedLength)
          processedLength = responseText.length

          if (newText) {
            handleSseText(newText)
          }
        },
      })

      if (buffer.trim()) {
        handleSseText('\n\n')
      }

      setChatRooms(prev =>
        moveChatRoomToTop(prev, currentSelectedChatId, chat => ({
          ...chat,
          openaiResponseId: nextOpenaiResponseId,
          preview: assistantContent || trimmedMessage,
          updatedAt: nowIso,
          lastMessageCreatedAt: nowIso,
        }))
      )
    } catch (e) {
      if (selectedChatIdRef.current === currentSelectedChatId) {
        const errorMessage = e.response.status === 429 ? '사용량 한도를 초과했어요. 24시간 후 다시 시도해 주세요.' : '응답을 불러오는 중 오류가 발생어요.'

        setMessages(prev => [
          ...prev,
          {
            mentoringMessageId: tempAssistantMessageId,
            role: ROLE.ASSISTANT,
            content: errorMessage,
            createdAt: nowIso,
          },
        ])
      }
    } finally {
      setTypingChatId(prev =>
        prev === currentSelectedChatId ? null : prev
      )
    }
  }

  return (
    <>
      <S.Background onClick={handleCloseModal} />

      <S.ModalBox height='76%' width='74%'>
        <CloseButtonArea>
          <CloseIcon
            className='close-icon'
            width='24px'
            onClick={handleCloseModal}
          />
        </CloseButtonArea>

        <ContentArea>
          <Sidebar>
            <SidebarHeader>
              <SidebarTitleRow>
                <AiIconBox>
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
                </AiIconBox>

                <SidebarTitleBox>
                  <SidebarTitle>AI 멘토링</SidebarTitle>
                  <SidebarSubTitle>학습에 관한 무엇이든 함께 해요</SidebarSubTitle>
                </SidebarTitleBox>
              </SidebarTitleRow>

              <NewChatButton type='button' onClick={handleCreateChat}>
                새 대화
              </NewChatButton>
            </SidebarHeader>

            <ChatRoomList>
              {chatRooms.map(chat => {
                const time = formatDisplayTime(chat.lastMessageCreatedAt)

                return (
                  <ChatRoomItem
                    key={chat.mentoringConversationId}
                    isActive={chat.mentoringConversationId === selectedChatId}
                    onClick={() => handleSelectChat(chat.mentoringConversationId)}
                  >
                    <ChatRoomItemTop>
                      <ChatRoomItemTitle isActive={chat.mentoringConversationId === selectedChatId}>
                        {chat?.title || '새 대화'}
                      </ChatRoomItemTitle>
                      <ChatRoomItemTime title={time.absolute}>
                        {time.relative}
                      </ChatRoomItemTime>
                    </ChatRoomItemTop>

                    <ChatRoomPreview>
                      {chat?.preview || '새로운 멘토링 대화를 시작해 보세요'}
                    </ChatRoomPreview>
                  </ChatRoomItem>
                )
              })}
            </ChatRoomList>
          </Sidebar>

          <MainSection>
            <ChatHeader>
              <ChatHeaderLeft>
                <AiIconBox>
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 17 }} />
                </AiIconBox>

                <ChatHeaderTextBox>
                  <ChatHeaderTitle>
                    {selectedChatRoom?.title || 'AI 멘토링'}
                  </ChatHeaderTitle>
                  <ChatHeaderDesc>
                    학습 계획, 진로 고민 등 AI 멘토에게 편하게 얘기해 보세요
                  </ChatHeaderDesc>
                </ChatHeaderTextBox>
              </ChatHeaderLeft>
            </ChatHeader>

            <ChatBody ref={chatBodyRef}>
              {chatRooms.length ===0 ? (
                <EmptyState>
                  <EmptyIconBox>
                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 26 }} />
                  </EmptyIconBox>
                  <EmptyTitle>대화를 먼저 생성해 주세요</EmptyTitle>
                  <EmptyDesc>
                    좌측 상단의 '새 대화'를 눌러 대화를 생성해 주세요
                  </EmptyDesc>
                </EmptyState>
              ): (
                messages.length === 0 ? (
                    <EmptyState>
                      <EmptyIconBox>
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: 26 }} />
                      </EmptyIconBox>
                      <EmptyTitle>새로운 대화를 시작해 보세요</EmptyTitle>
                      <EmptyDesc>
                        학습하며 막혔던 부분이나 고민들을 편하게 적어주세요
                      </EmptyDesc>
                    </EmptyState>
                  ) : (
                    <>
                      {messages.map(msg => {
                        const time = formatMessageTime(msg.createdAt)

                        return (
                          <MessageRow key={msg.mentoringMessageId} role={msg.role}>
                            {msg.role === ROLE.ASSISTANT && (
                              <AssistantAvatar>
                                <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
                              </AssistantAvatar>
                            )}

                            <MessageBubble role={msg.role}>
                              <MessageText>{msg.content}</MessageText>
                              <MessageTime>{moment(msg.createdAt).format('HH:mm')}</MessageTime>
                            </MessageBubble>
                          </MessageRow>
                        )
                      })}

                      {typingChatId === selectedChatId && (
                        <MessageRow role={ROLE.ASSISTANT}>
                          <AssistantAvatar>
                            <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
                          </AssistantAvatar>

                          <TypingBubble>
                            <TypingDot delay='0s' />
                            <TypingDot delay='0.15s' />
                            <TypingDot delay='0.3s' />
                          </TypingBubble>
                        </MessageRow>
                      )}
                    </>
                  )
              )}
            </ChatBody>

            <InputForm onSubmit={handleSubmit}>
              <InputWrapper>
                <ChatInput
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={
                    isStreamingCurrentChat
                      ? 'AI 멘토가 답변 생성 중입니다.'
                      : 'AI 멘토에게 질문을 입력해 주세요.'
                  }
                />
              </InputWrapper>

              <SendButton
                type='submit'
                disabled={isStreamingCurrentChat || !message.trim()}
              >
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 17 }} />
                {isStreamingCurrentChat ? '생성 중' : '전송'}
              </SendButton>
            </InputForm>
          </MainSection>
        </ContentArea>
      </S.ModalBox>
    </>
  )
}

const pulse = keyframes`
    0%, 80%, 100% {
        opacity: 0.35;
        transform: translateY(0);
    }
    40% {
        opacity: 1;
        transform: translateY(-3px);
    }
`

const CloseButtonArea = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 18px 22px 0 22px;

    .close-icon {
        cursor: pointer;
        flex-shrink: 0;
    }
`

const ContentArea = styled.div`
    height: calc(100% - 52px);
    display: flex;
    gap: 18px;
    padding: 14px 22px 22px 22px;
    box-sizing: border-box;
`

const Sidebar = styled.aside`
    width: 31%;
    min-width: 280px;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    border-radius: 16px;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

const SidebarHeader = styled.div`
    padding: 18px 16px 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
    background: #ffffff;
`

const SidebarTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
`

const flowBlue = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`

const AiIconBox = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: linear-gradient(
            135deg,
            #eef2ff 0%,
            #dbeafe 40%,
            #e0e7ff 70%,
            #f0f9ff 100%
    );
    background-size: 200% 200%;
    animation: ${flowBlue} 5s ease-in-out infinite;
    border: 1px solid rgba(59, 130, 246, 0.25);
    color: #1e3a8a;
    font-weight: 800;
    font-size: 13px;
    box-shadow: 0 2px 10px rgba(186, 204, 255, 0.3);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
`

const SidebarTitleBox = styled.div`
    min-width: 0;
`

const SidebarTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
`

const SidebarSubTitle = styled.p`
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #666666;
`

const NewChatButton = styled.button`
    width: 100%;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    border-radius: 10px;
    background: #ffffff;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: 0.2s ease;

    &:hover {
        border-color: ${({ theme }) => theme.colors.primary};
        background: #ffffff;
    }
`

const ChatRoomList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    background: #ffffff;
`

const ChatRoomItem = styled.button`
    width: 100%;
    border: 1px solid
    ${({ isActive, theme }) =>
            isActive ? theme.colors.skyblue : 'transparent'};
    background: ${({ isActive, theme }) =>
            isActive ? theme.colors.skyblue : '#ffffff'};
    border-radius: 10px;
    padding: 12px;
    text-align: left;
    cursor: pointer;
    margin-bottom: 8px;
    transition: 0.15s ease;

    &:hover {
        background: ${({ isActive, theme }) =>
                isActive ? theme.colors.skyblue : theme.colors.lightestGray};
    }
`

const ChatRoomItemTop = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
`

const ChatRoomItemTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${({ isActive, theme }) =>
            isActive ? theme.colors.primary : '#222222'};
`

const ChatRoomItemTime = styled.div`
    font-size: 11px;
    color: #777777;
    flex-shrink: 0;
`

const ChatRoomPreview = styled.div`
    font-size: 13px;
    color: #555555;
    line-height: 1.45;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`

const MainSection = styled.section`
    flex: 1;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    border-radius: 16px;
    background: #ffffff;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const ChatHeader = styled.div`
    padding: 18px 22px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
    background: #ffffff;
`

const ChatHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

const HeaderIconBox = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.skyblue};
    color: ${({ theme }) => theme.colors.primary};
    display: flex;
    align-items: center;
    justify-content: center;
`

const ChatHeaderTextBox = styled.div``

const ChatHeaderTitle = styled.h3`
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
`

const ChatHeaderDesc = styled.p`
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #666666;
`

const ChatBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 20px 22px;
    background: #ffffff;
`

const MessageRow = styled.div`
    display: flex;
    text-align: start;
    align-items: flex-end;
    justify-content: ${({ role }) =>
            role === ROLE.USER ? 'flex-end' : 'flex-start'};
    gap: 8px;
    margin-bottom: 14px;
`

const AssistantAvatar = styled.div`
    width: 30px;
    height: 30px;
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`

const MessageBubble = styled.div`
    max-width: 72%;
    padding: 12px 14px;
    border-radius: ${({ role }) =>
            role === ROLE.USER ? '12px 12px 4px 12px' : '12px 12px 12px 4px'};
    background: ${({ role, theme }) =>
            role === ROLE.USER ? theme.colors.lightestGray : '#ffffff'};
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
`

const MessageText = styled.div`
    font-size: 14px;
    color: #222222;
    line-height: 1.6;
    word-break: break-word;
    white-space: pre-wrap;
`

const MessageTime = styled.div`
    margin-top: 8px;
    font-size: 11px;
    color: #888888;
    text-align: right;
`

const TypingBubble = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    padding: 12px 14px;
    border-radius: 12px 12px 12px 4px;
    background: #ffffff;
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
`

const TypingDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    animation: ${pulse} 1s infinite ease-in-out;
    animation-delay: ${({ delay }) => delay};
`

const EmptyState = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
`

const EmptyIconBox = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: linear-gradient(
            135deg,
            #eef2ff 0%,
            #dbeafe 40%,
            #e0e7ff 70%,
            #f0f9ff 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    animation: ${flowBlue} 5s ease-in-out infinite;
    border: 1px solid rgba(59, 130, 246, 0.25);
    color: #1e3a8a;
    font-weight: 800;
    font-size: 13px;
    box-shadow: 0 2px 10px rgba(186, 204, 255, 0.3);
    transition: all 0.3s ease;

    &:active {
        transform: scale(0.96);
        filter: brightness(0.95);
    }
`

const EmptyTitle = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 8px;
`

const EmptyDesc = styled.div`
    font-size: 14px;
    color: #666666;
    line-height: 1.6;
`

const InputForm = styled.form`
    display: flex;
    gap: 10px;
    padding: 16px 22px 20px 22px;
    border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
    background: #ffffff;
`

const InputWrapper = styled.div`
    flex: 1;
`

const ChatInput = styled.input`
    width: 100%;
    height: 46px;
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    border-radius: 10px;
    padding: 0 14px;
    box-sizing: border-box;
    font-size: 14px;
    color: #222222;
    outline: none;
    background: #ffffff;
    transition: 0.15s ease;

    &::placeholder {
        color: #999999;
    }

    &:focus {
        border-color: ${({ theme }) => theme.colors.primary};
        background: #ffffff;
    }

    &:disabled {
        background: #f7f7f7;
        color: #999999;
        cursor: not-allowed;
    }
`

const SendButton = styled.button`
    min-width: 92px;
    height: 46px;
    border: none;
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: 0.15s ease;

    &:hover {
        opacity: 0.94;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`